import type { PolyRelayOpenOrder } from '@/lib/polymarket/relay';

export interface ManagerPolyFill {
  fill_id?: string | null;
  order_id?: string | null;
  ticker: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  count: number;
  price_cents: number;
  is_taker: boolean;
  fees_cents: number | null;
  executed_at: string;
}

export interface ManagerPolyPnl {
  realizedPnlCents: number;
  realizedFeesCents?: number | null;
  realizedPnlNetCents?: number | null;
  openCount: number;
  openNotionalCents: number;
  settlements?: Record<string, 'yes' | 'no' | 'void'>;
}

/**
 * The CLOB SDK has returned both a bare array and an object containing an
 * `orders` array across versions. The manager relay deliberately preserves
 * that upstream shape under `data`, so Trader Detail must normalize both
 * without guessing that an unfamiliar object means "no orders".
 */
export function unwrapManagerPolyOrders(payload: unknown): PolyRelayOpenOrder[] {
  if (!payload || typeof payload !== 'object') return [];
  const data = (payload as { data?: unknown }).data;
  if (Array.isArray(data)) return data as PolyRelayOpenOrder[];
  if (data && typeof data === 'object') {
    const orders = (data as { orders?: unknown }).orders;
    if (Array.isArray(orders)) return orders as PolyRelayOpenOrder[];
  }
  return [];
}

/**
 * A row the fills table can actually render.
 *
 * Every field checked here is required in the relay's response: `ticker`,
 * `side`, `action`, `count`, `is_taker` and `executed_at` are NOT NULL columns,
 * and `price_cents` is `COALESCE(price_cents_exact, price_cents)` over a
 * nullable exact price and a NOT NULL fallback, so it too always arrives. Only
 * `fees_cents` is genuinely nullable — null there means "fee unknown", NOT
 * "fee zero", so it is accepted as null and never coerced. (`fill_id` and
 * `order_id` are nullable identifiers the table only uses as a React key, so
 * they are not checked at all.)
 *
 * This checks the TYPE each field arrives as and never the value: `side` and
 * `action` are free-text columns that TypeScript merely narrows, so policing
 * them against 'yes'/'no' would reject a legitimate row and black the panel
 * out — a worse failure than the one this guard exists to prevent.
 *
 * `count` and `price_cents` are the load-bearing ones: the table calls
 * `.toLocaleString()` / `.toFixed()` on them, so a non-number row does not
 * merely render badly, it throws during render and — with no error boundary
 * over this route — blanks the whole page.
 */
function isManagerPolyFillRow(row: unknown): row is ManagerPolyFill {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
  const r = row as Record<string, unknown>;
  return (
    typeof r.ticker === 'string' &&
    typeof r.side === 'string' &&
    typeof r.action === 'string' &&
    typeof r.executed_at === 'string' &&
    typeof r.is_taker === 'boolean' &&
    Number.isFinite(r.count) &&
    Number.isFinite(r.price_cents) &&
    (r.fees_cents == null || Number.isFinite(r.fees_cents))
  );
}

/**
 * `GET /api/manager/traders/:id/polymarket/fills` answers `{ fills: [...] }` on
 * success, assembled row by row in the relay (its failure paths return an
 * `{ error }` body under a 4xx). So anything else on a 2xx — gateway HTML,
 * `{}`, an error envelope, a non-array `fills`, junk rows —
 * is a failed read, NOT a trader with no history. Returns `null` for those so
 * the caller can keep the last confirmed rows; returns the rows otherwise,
 * including a genuine `[]`, which IS authoritative and must clear the panel.
 *
 * Deliberately separate from this view's shared `toArr` helper, which the
 * Kalshi manager parses also use — hardening that would change them too.
 */
export function unwrapManagerPolyFills(payload: unknown): ManagerPolyFill[] | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const rows = (payload as { fills?: unknown }).fills;
  if (!Array.isArray(rows)) return null;
  if (!rows.every(isManagerPolyFillRow)) return null;
  return rows;
}

/** How the fills panel should disclose the read it just did. */
export type PolyFillsDisclosure = 'none' | 'retained' | 'unavailable';

export interface PolyFillsPanel {
  fills: ManagerPolyFill[];
  /** True once a read has been confirmed — retention is only honest after that. */
  confirmed: boolean;
  disclosure: PolyFillsDisclosure;
}

export const EMPTY_POLY_FILLS_PANEL: PolyFillsPanel = {
  fills: [],
  confirmed: false,
  disclosure: 'none',
};

/**
 * The whole retention decision for the fills panel, in one place so the tests
 * exercise the choice the view actually makes rather than a re-implementation
 * of it. `decoded` is the decoder's answer for a 2xx body, or `null` for either
 * a malformed 2xx or a transport/HTTP failure — both are "unavailable", never
 * "empty".
 *
 * Retention is only claimed once a read has been confirmed. On a first-load
 * failure there is nothing to retain, so the panel stays blank and says so —
 * the same rule `RetainedDataBanner` applies via its `ageMs != null` guard
 * (#125), which exists to stop a never-loaded surface claiming retained rows.
 */
export function nextPolyFillsPanel(
  prev: PolyFillsPanel,
  decoded: ManagerPolyFill[] | null,
): PolyFillsPanel {
  if (decoded) return { fills: decoded, confirmed: true, disclosure: 'none' };
  if (prev.confirmed) return { ...prev, disclosure: 'retained' };
  return { fills: [], confirmed: false, disclosure: 'unavailable' };
}

/**
 * CLOB prices are normally 0..1 dollar strings; tolerate already-cent values.
 * The `<= 1.01` dollars-vs-cents split is a heuristic and inherently ambiguous
 * for values in (1, 1.01] — a CLOB dollar price never exceeds 1.0, so anything
 * above that must already be cents; 1.005¢ as a cents input would be
 * misread as dollars, but no venue prices there today. Sub-cent precision is
 * preserved to the 0.01¢ grain (0.001 → 0.1¢) — no whole-cent rounding.
 */
export function polyPriceCents(value: unknown): number | null {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number.parseFloat(value)
        : NaN;
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed <= 1.01
    ? Math.round(parsed * 10000) / 100
    : Math.round(parsed * 100) / 100;
}

/** CLOB timestamps have appeared as seconds, milliseconds, and ISO strings. */
export function polyTimestamp(value: unknown): Date | null {
  if (typeof value === 'number' || (typeof value === 'string' && /^\d+(?:\.\d+)?$/.test(value))) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    const millis = parsed < 10_000_000_000 ? parsed * 1_000 : parsed;
    const date = new Date(millis);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string' && value !== '') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}
